import React, { useContext, useEffect, useState } from 'react'
import { Radio, Users as IconUsers, X as IconClose } from 'react-feather'
import '../App.css'
import css from './Members.module.css'
import toast, { Toaster } from 'react-hot-toast'

import { ScaleLoader } from 'react-spinners'
import SecondaryNav from '../components/navigation/SecondaryNavigation/SecondaryNav'
import usePeopleManagement from '../hooks/usePeopleManagement'
import { AppContext } from '../context/AppContext'
import MemberPermissions from '../components/modals/MemberPermissions'
import RemoveMember from '../components/modals/RemoveMember'
import RestoreMember from '../components/modals/RestoreMember'
import { GymMemberData } from '../types/types'
import { UserContext } from '../context/UserContext'
import Footer from '../components/sections/Footer/Footer'
import Page from './technical/Page'
import { BasePageState, usePageContext } from '../context/PageContext'
import EngagementStats from '../components/sections/EngagementStats/EngagementStats'
import useGymEngagement, {
  useGymMonthlyActivity,
} from '../hooks/useGymEngagement'
import ActivityChart from '../components/sections/ActivityChart/ActivityChart'
import {
  ActivityHistoryList,
  MemberRowsSkeleton,
  MemberSort,
  SortedMemberList,
} from '../components/sections/MemberGroups/MemberGroups'
import HeaderTabs from '../components/navigation/HeaderTabs/HeaderTabs'
import SortSelect from '../components/inputs/SortSelect/SortSelect'
import SearchInput from '../components/inputs/SearchInput/SearchInput'
import matchesName from '../utilities/matchesName'

type MemberTab = 'members' | 'staff' | 'blocked'

const Members = () => {
  const [modal, setModal] = useState<{
    data: GymMemberData
    type: 'edit-permissions' | 'remove-member' | 'restore-member'
  } | null>(null)

  const [modalIsVisible, setModalIsVisible] = useState<boolean>(false)
  const { userID } = useContext(UserContext)
  const { setLoading } = usePageContext()

  const onUpdateSuccess = () => {
    toast.success('Member updated successfuly!')
    dismissManageModal()
  }

  const onUpdateFailure = () => {
    toast.error('Something went wrong. Please try again')
  }

  const { currentSpace } = useContext(AppContext)
  const {
    members,
    peopleAreLoading,
    updateInProgress,
    removedCount,
    removedMembersAreLoading,
    restoreMember,
    updateMemberData,
    changeMemberAccess,
    loadRemovedMembers,
  } = usePeopleManagement({
    currentSpace: currentSpace && currentSpace.id,
    onUpdateSuccess: onUpdateSuccess,
    onUpdateFailure: onUpdateFailure,
    loadRemovedLazily: true,
  })

  const [tab, setTab] = useState<MemberTab>('members')
  // Null until picked: the default depends on whether views can be shown
  const [pickedSort, setPickedSort] = useState<MemberSort | null>(null)
  // Filters the open tab in the browser: no extra reads
  const [search, setSearch] = useState('')

  useEffect(() => {
    setLoading(peopleAreLoading)
  }, [peopleAreLoading])

  const canViewEngagement =
    !!currentSpace &&
    (currentSpace.ownerID === userID ||
      !!currentSpace.permissions?.canEditMembers)

  // Always newest first. Viewed needs the stats, so it's not offered without
  const sortOptions: { value: MemberSort; label: string }[] = [
    { value: 'tracked', label: 'Last tracked' },
    ...(canViewEngagement
      ? [{ value: 'viewed' as const, label: 'Last viewed' }]
      : []),
    { value: 'joined', label: 'Last joined' },
  ]
  // Last viewed by default when the stats are available, else last tracked
  const defaultSort: MemberSort = canViewEngagement ? 'viewed' : 'tracked'
  const sort =
    sortOptions.find((option) => option.value === pickedSort)?.value ??
    defaultSort

  const { engagement, engagementIsLoading } = useGymEngagement(
    currentSpace?.id || null,
    canViewEngagement
  )
  const monthlyActivity = useGymMonthlyActivity(
    currentSpace?.id || null,
    canViewEngagement
  )
  const showChart = canViewEngagement && !!currentSpace

  const manageMember = (action: string, data: GymMemberData) => {
    switch (action) {
      case 'edit': {
        setModal({ data: data, type: 'edit-permissions' })
        setTimeout(() => {
          //without timeout it won't show CSS animation
          //when appears
          setModalIsVisible(true)
        }, 0)
        break
      }

      case 'remove': {
        setModal({ data: data, type: 'remove-member' })
        setTimeout(() => {
          //without timeout it won't show CSS animation
          //when appears
          setModalIsVisible(true)
        }, 0)
        break
      }

      case 'restore': {
        setModal({ data: data, type: 'restore-member' })
        setTimeout(() => {
          //without timeout it won't show CSS animation
          //when appears
          setModalIsVisible(true)
        }, 0)
        break
      }
    }
  }

  const dismissManageModal = () => {
    setModalIsVisible(false)
    setTimeout(() => {
      setModal(null)
    }, 250)
  }

  const managers =
    members && members.filter((m) => m.role === 'manager' && m.hasAccess)
  const coaches =
    members && members.filter((m) => m.role === 'coach' && m.hasAccess)

  const removed = members && members.filter((m) => !m.hasAccess)

  const joined = members.filter((m) => m.hasAccess)
  // Owner, managers, coaches, and anyone else with a special permission
  // (edit workouts, members or permissions, remove comments)
  const staff = joined.filter(
    (m) =>
      m.role !== 'athlete' ||
      Object.values(m.permissions || {}).some((granted) => granted === true)
  )

  const allTabMembers =
    tab === 'blocked' ? removed : tab === 'staff' ? staff : joined
  const isSearching = !!search.trim()
  const tabMembers = isSearching
    ? allTabMembers.filter((m) => matchesName(m.name, search))
    : allTabMembers
  // Blocked members load on first open; the server count shows until then
  const tabTotal = tab === 'blocked' ? removedCount : allTabMembers.length
  const memberCountText = isSearching
    ? `${tabMembers.length.toLocaleString()} of ${formatMemberCount(tabTotal)}`
    : formatMemberCount(tabTotal)

  return (
    <>
      <Toaster
        toastOptions={{
          success: {
            iconTheme: {
              primary: 'var(--accent-green)',
              secondary: 'white',
            },
            style: {
              borderRadius: '8px',
            },
          },
          error: {
            iconTheme: {
              primary: 'var(--accent-red)',
              secondary: 'white',
            },
            style: {
              borderRadius: '8px',
            },
          },
        }}
      />

      {modal &&
        (modal.type === 'edit-permissions' ? (
          <MemberPermissions
            data={modal.data}
            isVisible={modalIsVisible}
            isLoading={updateInProgress}
            onCancel={dismissManageModal}
            onConfirm={updateMemberData}
          />
        ) : modal.type === 'remove-member' ? (
          <RemoveMember
            data={modal.data}
            isVisible={modalIsVisible}
            isLoading={updateInProgress}
            onCancel={dismissManageModal}
            onConfirm={(data) =>
              changeMemberAccess({ data: data, hasAccess: false })
            }
          />
        ) : (
          <RestoreMember
            data={modal.data}
            isVisible={modalIsVisible}
            isLoading={updateInProgress}
            onCancel={dismissManageModal}
            onConfirm={restoreMember}
          />
        ))}

      <Page isLoading={peopleAreLoading}>
        <div
          id="page-content"
          className={`col-8 ${showChart ? css['with-chart'] : ''}`}
        >
          {!peopleAreLoading ? (
            <div className={css['layout']}>
              {canViewEngagement && currentSpace && (
                <div className={css['stats']}>
                  <EngagementStats
                    members={joined}
                    engagement={engagement}
                    isLoading={engagementIsLoading}
                  />
                </div>
              )}

              {showChart && (
                <aside className={css['aside']}>
                  <ActivityChart
                    activity={monthlyActivity.data}
                    isLoading={monthlyActivity.isLoading}
                  />
                </aside>
              )}

              <div className={css['members']}>
                {/* Member list. Not tied to the activity period above */}
                <HeaderTabs<MemberTab>
                  label="Members"
                  tabs={[
                    { value: 'members', label: 'All members' },
                    { value: 'staff', label: 'Staff' },
                    // Always shown, even when empty, so the tab you're on
                    // never disappears (e.g. after restoring the last one)
                    { value: 'blocked', label: 'Blocked' },
                  ]}
                  value={tab}
                  onChange={(newValue) => {
                    // Blocked members' names and photos load on first open
                    if (newValue === 'blocked') loadRemovedMembers()
                    setTab(newValue)
                  }}
                  toolbar={
                    <>
                      {/* Count next to search: it's the search's feedback, and
                        growing to the right it never moves the controls */}
                      <div className={css['search']}>
                        <SearchInput
                          value={search}
                          onChange={setSearch}
                          placeholder="Search by name"
                        />
                        <span className={css['count']} aria-live="polite">
                          {memberCountText}
                        </span>
                      </div>
                      <SortSelect<MemberSort>
                        options={sortOptions}
                        value={sort}
                        onChange={setPickedSort}
                      />
                    </>
                  }
                />

                {tab === 'blocked' &&
                removedMembersAreLoading &&
                removedCount > 0 ? (
                  <section>
                    <MemberRowsSkeleton
                      rows={Math.min(removedCount, 5)}
                      startIndex={1}
                    />
                  </section>
                ) : isSearching && !tabMembers.length ? (
                  <section>
                    <p className={css['empty']}>
                      No members match “{search.trim()}”
                    </p>
                  </section>
                ) : !tabMembers.length ? (
                  <section>
                    <p className={css['empty']}>
                      {tab === 'blocked'
                        ? 'No blocked members'
                        : tab === 'staff'
                        ? 'No staff yet'
                        : 'No members yet'}
                    </p>
                  </section>
                ) : sort !== 'viewed' || !currentSpace ? (
                  <SortedMemberList
                    // Start each tab and sort from the first batch
                    key={`${tab}-${sort}`}
                    gymID={canViewEngagement ? currentSpace?.id || null : null}
                    members={tabMembers}
                    sort={sort === 'joined' ? 'joined' : 'tracked'}
                    // By month, like the Viewed order; one list for search
                    // results
                    isGrouped={!isSearching}
                    onUserAction={manageMember}
                  />
                ) : (
                  <ActivityHistoryList
                    key={tab}
                    gymID={currentSpace.id}
                    members={tabMembers}
                    isGrouped={!isSearching}
                    onUserAction={manageMember}
                  />
                )}

                {tab === 'members' &&
                  !isSearching &&
                  currentSpace &&
                  currentSpace.ownerID === userID &&
                  !managers.length &&
                  !coaches.length && (
                    <section>
                      <div
                        style={{
                          height: '80px',
                          color: 'var(--secondary-text)',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderTop: '1px solid var(--divider)',
                          paddingTop: '24px',
                          marginTop: '-24px',
                        }}
                        className={'placeholder'}
                      >
                        Assign managers or coaches by changing the roles of
                        existing members.
                      </div>
                    </section>
                  )}
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </Page>
    </>
  )
}

const formatMemberCount = (count: number) =>
  `${count.toLocaleString()} ${count === 1 ? 'member' : 'members'}`

export default Members
